import React from "react";
import { FiClock, FiCornerUpLeft } from "react-icons/fi";
import { Modal } from "../Modal";
import type { ComponentHistoryEntry } from "./useComponentHistory";

type PageEditorHistoryModalProps = {
	show: boolean;
	onClose: () => void;
	entries: ComponentHistoryEntry[];
	currentIndex: number;
	onJump: (index: number) => void;
};

/** French date + time label for a history entry (e.g. « 13 juil. 2026, 14:32:07 »). */
function formatTimestamp(at: number): string {
	try {
		return new Date(at).toLocaleString("fr-FR", {
			day: "2-digit",
			month: "short",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit"
		});
	} catch {
		return "";
	}
}

/**
 * Modal timeline of the current editing session's recorded states. Entries are
 * shown newest-first with their timestamp; clicking one jumps the editor
 * straight to that state (non-destructive : nothing is discarded).
 */
const PageEditorHistoryModal: React.FC<PageEditorHistoryModalProps> = ({
	show,
	onClose,
	entries,
	currentIndex,
	onJump
}) => {
	// Newest first for scanning; keep the original index for jumpTo.
	const rows = entries.map((entry, index) => ({ entry, index })).reverse();

	return (
		<Modal show={show} onHide={onClose} size="md" centered>
			<Modal.Header closeButton className="rounded-t-xl bg-slate-50">
				<div className="space-y-0.5">
					<Modal.Title className="text-lg font-semibold text-slate-900">Historique de la session</Modal.Title>
					<p className="text-xs text-slate-500">
						Cliquez sur un état pour y revenir. Rien n'est supprimé : vous pouvez repartir de n'importe quel point.
					</p>
				</div>
			</Modal.Header>
			<Modal.Body className="bg-white">
				{rows.length <= 1 ? (
					<p className="py-8 text-center text-sm text-slate-500">
						Aucune modification enregistrée pour l'instant.
					</p>
				) : (
					<ol className="space-y-1.5">
						{rows.map(({ entry, index }) => {
							const isCurrent = index === currentIndex;
							const isBaseline = index === 0;
							return (
								<li key={index}>
									<button
										type="button"
										onClick={() => {
											onJump(index);
											onClose();
										}}
										aria-current={isCurrent}
										className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition ${
											isCurrent
												? "border-[#005CA9] bg-[#005CA9]/[0.04] ring-1 ring-[#005CA9]"
												: "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
										}`}
									>
										<span className="flex min-w-0 items-center gap-2.5">
											<FiClock className="shrink-0 text-slate-400" aria-hidden />
											<span className="min-w-0">
												<span className="block text-sm font-semibold text-slate-800">
													{formatTimestamp(entry.at)}
												</span>
												<span className="block text-xs text-slate-500">
													{isBaseline ? "État initial · " : ""}
													{entry.count} composant{entry.count > 1 ? "s" : ""}
												</span>
											</span>
										</span>
										{isCurrent ? (
											<span className="shrink-0 rounded-full bg-[#005CA9] px-2 py-0.5 text-[11px] font-semibold text-white">
												Actuel
											</span>
										) : (
											<span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-[#005CA9]">
												<FiCornerUpLeft aria-hidden /> Revenir
											</span>
										)}
									</button>
								</li>
							);
						})}
					</ol>
				)}
			</Modal.Body>
		</Modal>
	);
};

export default PageEditorHistoryModal;
